import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '../components'
import type { AppCreate } from '../types'

interface CreateAppModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: AppCreate) => Promise<void>
}

export default function CreateAppModal({ isOpen, onClose, onSubmit }: CreateAppModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [healthUrl, setHealthUrl] = useState('')
  const [pingInterval, setPingInterval] = useState(5)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setIsSubmitting(true)
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        health_url: healthUrl.trim() || undefined,
        ping_interval_minutes: pingInterval,
      })
      setName('')
      setDescription('')
      setHealthUrl('')
      setPingInterval(5)
      onClose()
    } catch {
      // Error already handled by AppsContext (toast shown)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-slate-200 rounded-xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Create New App</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">App Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="My Application"
              className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of your app"
              className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Health Check URL
            </label>
            <input
              type="url"
              value={healthUrl}
              onChange={e => setHealthUrl(e.target.value)}
              placeholder="https://myapp.com/health"
              className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave empty to disable uptime monitoring. You can add this later in settings.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Ping Interval (minutes)
            </label>
            <select
              value={pingInterval}
              onChange={e => setPingInterval(Number(e.target.value))}
              className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value={1}>Every 1 minute</option>
              <option value={5}>Every 5 minutes</option>
              <option value={15}>Every 15 minutes</option>
              <option value={30}>Every 30 minutes</option>
              <option value={60}>Every hour</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!name.trim() || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create App'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
